import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import {
  createUser,
  deleteUser,
  getUsers,
  updateUser,
  updateUserPassword,
} from "../api/userApi";

import { getInstitutions } from "../api/institutionApi";

const { Title } = Typography;

function getRoleColor(role) {
  if (role === "ADMIN") return "red";
  if (role === "MODERATOR") return "blue";
  if (role === "AUTHOR") return "gold";
  return "default";
}

function mapInstitutionOptions(data) {
  return (data || [])
    .map((item) => item?.institutionResponse || item)
    .filter(
      (institution) =>
        institution?.id &&
        institution?.type === "TRADING_HOUSE"
    )
    .map((institution) => ({
      label: institution.name || "-",
      value: institution.id,
      rawName: institution.name || "-",
      rawType: institution.type || "-",
      isActive: institution.is_active,
    }))
    .sort((a, b) => a.rawName.localeCompare(b.rawName));
}

function extractUserInstitutionIds(user) {
  if (!user) return [];

  if (Array.isArray(user.tradehouseIds)) {
    return user.tradehouseIds.filter(Boolean);
  }

  if (Array.isArray(user.institutionIds)) {
    return user.institutionIds.filter(Boolean);
  }

  if (Array.isArray(user.tradehouses)) {
    return user.tradehouses.map((item) => item?.id).filter(Boolean);
  }

  if (Array.isArray(user.institutions)) {
    return user.institutions.map((item) => item?.id).filter(Boolean);
  }

  return [];
}

function extractUserInstitutionNames(user) {
  if (!user) return [];

  if (Array.isArray(user.tradehouseNames) && user.tradehouseNames.length) {
    return user.tradehouseNames.filter(Boolean);
  }

  if (Array.isArray(user.institutionNames) && user.institutionNames.length) {
    return user.institutionNames.filter(Boolean);
  }

  if (Array.isArray(user.tradehouses) && user.tradehouses.length) {
    return user.tradehouses
      .map((item) => item?.name || item?.tradehouseName || item?.title)
      .filter(Boolean);
  }

  if (Array.isArray(user.institutions) && user.institutions.length) {
    return user.institutions
      .map((item) => item?.name || item?.institutionName || item?.title)
      .filter(Boolean);
  }

  return [];
}

function normalizeUsers(data) {
  return (data || []).map((item) => ({
    ...item,
    key: item.id,
  }));
}

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [institutionsLoading, setInstitutionsLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const createRole = Form.useWatch("role", createForm);
  const editRole = Form.useWatch("role", editForm);

  const institutionMap = useMemo(() => {
    const map = new Map();
    institutionOptions.forEach((item) => {
      map.set(item.value, item);
    });
    return map;
  }, [institutionOptions]);

  const adminUser = useMemo(
    () => users.find((user) => user.role === "ADMIN") || null,
    [users],
  );

  const hasAdmin = !!adminUser;

  const createRoleOptions = useMemo(() => {
    const base = [
      { label: "MODERATOR", value: "MODERATOR" },
      { label: "AUTHOR", value: "AUTHOR" },
      { label: "CUSTOMER", value: "CUSTOMER" },
    ];

    if (!hasAdmin) {
      return [{ label: "ADMIN", value: "ADMIN" }, ...base];
    }

    return base;
  }, [hasAdmin]);

  const getEditRoleOptions = (user) => {
    const base = [
      { label: "MODERATOR", value: "MODERATOR" },
      { label: "AUTHOR", value: "AUTHOR" },
      { label: "CUSTOMER", value: "CUSTOMER" },
    ];

    if (!hasAdmin) {
      return [{ label: "ADMIN", value: "ADMIN" }, ...base];
    }

    if (user?.role === "ADMIN") {
      return [{ label: "ADMIN", value: "ADMIN" }, ...base];
    }

    return base;
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const data = await getUsers();
      setUsers(normalizeUsers(data));
    } catch (error) {
      message.error(error.message || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const loadInstitutions = async () => {
    try {
      setInstitutionsLoading(true);

      const response = await getInstitutions();
      const raw = Array.isArray(response) ? response : response?.data || [];

      setInstitutionOptions(mapInstitutionOptions(raw));
    } catch (error) {
      message.error(error.message || "Failed to load tradehouses");
    } finally {
      setInstitutionsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadInstitutions();
  }, []);

  const openCreateModal = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      username: "",
      password: "",
      role: hasAdmin ? "AUTHOR" : "ADMIN",
      tradehouseIds: [],
    });
    setCreateOpen(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      username: user.username,
      role: user.role,
      tradehouseIds: extractUserInstitutionIds(user),
    });
    setEditOpen(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    passwordForm.resetFields();
    setPasswordOpen(true);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    createForm.resetFields();
  };

  const closeEditModal = () => {
    setEditOpen(false);
    setSelectedUser(null);
    editForm.resetFields();
  };

  const closePasswordModal = () => {
    setPasswordOpen(false);
    setSelectedUser(null);
    passwordForm.resetFields();
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();

      if (values.role === "ADMIN" && hasAdmin) {
        message.error("Only one ADMIN is allowed");
        return;
      }

      const payload = {
        username: values.username,
        password: values.password,
        role: values.role,
        institutionIds:
          values.role === "ADMIN" ? [] : values.tradehouseIds || [],
      };

      await createUser(payload);
      message.success("User created");
      closeCreateModal();
      loadUsers();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || "Failed to create user");
    }
  };

  const handleEdit = async () => {
    try {
      const values = await editForm.validateFields();

      const changingAnotherUserToAdmin =
        values.role === "ADMIN" &&
        hasAdmin &&
        adminUser?.id !== selectedUser?.id;

      if (changingAnotherUserToAdmin) {
        message.error("Only one ADMIN is allowed");
        return;
      }

      const payload = {
        username: values.username,
        role: values.role,
        institutionIds:
          values.role === "ADMIN" ? [] : values.tradehouseIds || [],
      };

      await updateUser(selectedUser.id, payload);
      message.success("User updated");
      closeEditModal();
      loadUsers();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || "Failed to update user");
    }
  };

  const handlePasswordUpdate = async () => {
    try {
      const values = await passwordForm.validateFields();

      await updateUserPassword(selectedUser.id, {
        password: values.password,
      });

      message.success("Password updated");
      closePasswordModal();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || "Failed to update password");
    }
  };

  const handleDelete = async (user) => {
    try {
      if (user.role === "ADMIN") {
        message.error("ADMIN user cannot be deleted");
        return;
      }

      await deleteUser(user.id);
      message.success("User deleted");
      loadUsers();
    } catch (error) {
      message.error(error.message || "Failed to delete user");
    }
  };

  const renderInstitutionTags = (record) => {
    if (record.role === "ADMIN") {
      return <Tag color="green">ALL</Tag>;
    }

    const namesFromBackend = extractUserInstitutionNames(record);
    if (namesFromBackend.length) {
      return (
        <Space size={[4, 4]} wrap>
          {namesFromBackend.map((name, index) => (
            <Tag key={`${name}-${index}`}>{name}</Tag>
          ))}
        </Space>
      );
    }

    const ids = extractUserInstitutionIds(record);
    if (!ids.length) {
      return "-";
    }

    return (
      <Space size={[4, 4]} wrap>
        {ids.map((id) => (
          <Tag key={id}>{institutionMap.get(id)?.rawName || id}</Tag>
        ))}
      </Space>
    );
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 220,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 140,
      render: (value) => <Tag color={getRoleColor(value)}>{value}</Tag>,
    },
    {
      title: "Tradehouses",
      key: "tradehouses",
      width: 360,
      render: (_, record) => renderInstitutionTags(record),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (value) => (value ? String(value).replace("T", " ") : "-"),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (value) => (value ? String(value).replace("T", " ") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 260,
      render: (_, record) => (
        <Space wrap>
          <Button size="small" onClick={() => openEditModal(record)}>
            Edit
          </Button>

          <Button size="small" onClick={() => openPasswordModal(record)}>
            Password
          </Button>

          <Popconfirm
            title="Delete user?"
            description={
              record.role === "ADMIN"
                ? "ADMIN user cannot be deleted"
                : `Delete ${record.username}?`
            }
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            disabled={record.role === "ADMIN"}
          >
            <Button danger size="small" disabled={record.role === "ADMIN"}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between">
          <Title level={3} className="!mb-0">
            Users
          </Title>

          <Space>
            {adminUser && <Tag color="red">ADMIN: {adminUser.username}</Tag>}

            <Button type="primary" onClick={openCreateModal}>
              Add User
            </Button>
          </Space>
        </div>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={usersLoading}
          bordered
          scroll={{ x: 1400 }}
        />
      </Card>

      <Modal
        open={createOpen}
        title="Add User"
        onCancel={closeCreateModal}
        onOk={handleCreate}
        okText="Create"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: "Username is required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select
              options={createRoleOptions}
              onChange={(value) => {
                if (value === "ADMIN") {
                  createForm.setFieldValue("tradehouseIds", []);
                }
              }}
            />
          </Form.Item>

          {createRole !== "ADMIN" && (
            <Form.Item
              name="tradehouseIds"
              label="Tradehouses"
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: "Select at least one tradehouse",
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Select tradehouses"
                options={institutionOptions}
                loading={institutionsLoading}
                optionFilterProp="label"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        open={editOpen}
        title="Edit User"
        onCancel={closeEditModal}
        onOk={handleEdit}
        okText="Save"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: "Username is required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select
              options={getEditRoleOptions(selectedUser)}
              onChange={(value) => {
                if (value === "ADMIN") {
                  editForm.setFieldValue("tradehouseIds", []);
                }
              }}
            />
          </Form.Item>

          {editRole !== "ADMIN" && (
            <Form.Item
              name="tradehouseIds"
              label="Tradehouses"
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: "Select at least one tradehouse",
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Select tradehouses"
                options={institutionOptions}
                loading={institutionsLoading}
                optionFilterProp="label"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        open={passwordOpen}
        title={`Change Password${selectedUser ? ` - ${selectedUser.username}` : ""}`}
        onCancel={closePasswordModal}
        onOk={handlePasswordUpdate}
        okText="Update"
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="password"
            label="New Password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UsersPage;
